import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { trackChatbotInteraction } from '../../utils/analytics';
import './chatbot.css';

const ChatBot = () => {
    const { theme } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: "Hi! I'm your AI assistant for this portfolio. Ask me anything about the projects, skills, or experience!"
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [hasGreeted, setHasGreeted] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
        
        // Track chatbot open/close
        if (isOpen) {
            trackChatbotInteraction('open');
        }
    }, [isOpen]);

    // Auto-open chatbot after 3 seconds on first visit
    useEffect(() => {
        const hasVisited = localStorage.getItem('chatbot_visited');
        if (!hasVisited && !hasGreeted) {
            const timer = setTimeout(() => {
                setIsOpen(true);
                setHasGreeted(true);
                localStorage.setItem('chatbot_visited', 'true');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [hasGreeted]);

    const quickQuestions = [
        "What are your main skills?",
        "Tell me about your projects",
        "What's your experience with AWS?",
        "How can I contact you?"
    ];

    const handleSendMessage = async (message = inputMessage) => {
        if (!message.trim()) return;

        // Track message sent
        trackChatbotInteraction('message_sent', 'user');

        const userMessage = message.trim();
        setInputMessage('');
        
        // Add user message
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsTyping(true);

        // Call your AWS Lambda function that handles OpenAI API
        const apiUrl = process.env.REACT_APP_AI_CHATBOT_API;
        
        if (!apiUrl || apiUrl === 'YOUR_LAMBDA_FUNCTION_URL') {
            const fallbackResponse = getFallbackResponse(userMessage);
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: fallbackResponse 
            }]);
            setIsTyping(false);
            return;
        }

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    messages: [...messages, { role: 'user', content: userMessage }]
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API returned ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            
            const assistantMessage = data.message || data.response || "I'm having trouble responding right now. Please try again!";

            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: assistantMessage 
            }]);
        } catch (error) {
            console.error('❌ Chatbot error:', error);
            console.error('Error details:', error.message);
            
            // Fallback response with knowledge base
            const fallbackResponse = getFallbackResponse(userMessage);
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                content: fallbackResponse 
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    const getFallbackResponse = (question) => {
        const lowerQuestion = question.toLowerCase();
        
        if (lowerQuestion.includes('skill') || lowerQuestion.includes('technology')) {
            return "The portfolio showcases expertise in:\n\n• Cloud & DevOps: AWS (Lambda, DynamoDB, S3, SES), Docker, CI/CD\n• Backend: Python, Node.js, API development\n• Frontend: React, JavaScript, responsive design\n• Databases: DynamoDB, SQL\n• Tools: Git, Linux, Automation\n\nCheck out the About section for more details!";
        }
        
        if (lowerQuestion.includes('project') || lowerQuestion.includes('work')) {
            return "The portfolio features projects in:\n\n• DevOps & CI/CD pipelines\n• Cloud Infrastructure (AWS)\n• Automation solutions\n• Web Development\n\nVisit the Projects section to explore each one in detail with live demos and source code!";
        }
        
        if (lowerQuestion.includes('aws') || lowerQuestion.includes('cloud')) {
            return "Strong AWS expertise demonstrated through:\n\n• Lambda functions for serverless computing\n• DynamoDB for database management\n• S3 & CloudFront for hosting\n• API Gateway for RESTful APIs\n• SES for email services\n\nMultiple AWS-powered projects are showcased in the portfolio!";
        }
        
        if (lowerQuestion.includes('contact') || lowerQuestion.includes('reach') || lowerQuestion.includes('email')) {
            return "You can reach out through:\n\n• Contact form in the Contact section\n• Newsletter subscription for updates\n• LinkedIn and GitHub (links in the sidebar)\n\nI'd love to hear from you!";
        }
        
        if (lowerQuestion.includes('experience') || lowerQuestion.includes('background')) {
            return "The portfolio demonstrates hands-on experience in:\n\n• Full-stack development\n• Cloud architecture and DevOps\n• Automation and scripting\n• Database design and optimization\n\nCheck the Resume section for detailed work history and the About section for more background!";
        }
        
        return "I can help you learn about:\n\n• Technical skills and expertise\n• Project showcases and demos\n• AWS and cloud experience\n• Contact information\n\nTry asking about any of these topics, or explore the portfolio sections!";
    };

    const handleQuickQuestion = (question) => {
        handleSendMessage(question);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <>
            {/* Chat Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`chatbot-toggle ${isOpen ? 'open' : ''} ${theme === 'dark' ? 'dark' : 'light'}`}
                aria-label="Toggle AI Assistant"
            >
                {isOpen ? (
                    <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                ) : (
                    <>
                        <svg className="w-5 h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <span className="chatbot-badge">AI</span>
                    </>
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className={`chatbot-window ${theme === 'dark' ? 'dark' : 'light'}`}>
                    {/* Header */}
                    <div className="chatbot-header">
                        <div className="chatbot-header-content">
                            <div className="chatbot-avatar">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                </svg>
                            </div>
                            <div>
                                <h3>AI Portfolio Assistant</h3>
                                <p className="chatbot-status">
                                    <span className="status-dot"></span>
                                    Online
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setIsOpen(false)}
                            className="chatbot-header-close"
                            aria-label="Close chat"
                            type="button"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="chatbot-messages">
                        {messages.map((msg, index) => (
                            <div key={index} className={`message ${msg.role}`}>
                                <div className="message-content">
                                    {msg.content.split('\n').map((line, i) => (
                                        <React.Fragment key={i}>
                                            {line}
                                            {i < msg.content.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        ))}
                        
                        {isTyping && (
                            <div className="message assistant">
                                <div className="message-content typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Questions */}
                    {messages.length === 1 && (
                        <div className="quick-questions">
                            <p className="quick-questions-label">Quick questions:</p>
                            <div className="quick-questions-grid">
                                {quickQuestions.map((question, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleQuickQuestion(question)}
                                        className="quick-question-btn"
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input */}
                    <div className="chatbot-input-container">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask me anything..."
                            className="chatbot-input"
                            disabled={isTyping}
                        />
                        <button
                            onClick={() => handleSendMessage()}
                            disabled={!inputMessage.trim() || isTyping}
                            className="chatbot-send-btn"
                            aria-label="Send message"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatBot;
